import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { type JSX, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import {
  getOrderFormRate,
  getOrderFormRateType,
  getOrderFormRateValue,
} from '~pages/Sales/Orders/NewOrderForm/utils';
import TrucksV2 from '~pages/Sales/Orders/OrderFormV2/components/TrucksV2';
import FreightRates from '~pages/Sales/Projects/components/FreightRates';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/** The panel's four checkboxes share the legacy compact styling verbatim. */
const checkboxSx = {
  [`& .${formControlLabelClasses.root}`]: { mr: 0 },
  [`& .${checkboxClasses.root}`]: { pr: '2px', py: 0 },
  [`& .${typographyClasses.root}`]: { fontSize: '12px', fontWeight: 400 },
} as const;

/**
 * Renders `form_fields.trucks`. Render-only: the embedded TrucksV2 exactly
 * as legacy mounts it (truck count/equipment/loads fields live inside the
 * embed), the four FF-gated checkboxes, the footer whose layout legacy
 * keys on the tomlinsonOrderModal and driverAgent flags, and the
 * fully-wired FreightRates manager on the context's `freightRatesRef`.
 * Every FreightRates prop is sourced from context — the rate-card-flag
 * conjunction legacy prefixes onto stripSrsIds drops because the dispatch
 * guarantees that flag is ON in v3; the formula remainders ship verbatim.
 */
export function TrucksPanel(): JSX.Element {
  const {
    applyRateChangesDefaultOn,
    callbacks,
    canEditProject,
    completedOrderRules,
    completedOrderRulesLoaded,
    showDefaultDigitalTicketView,
    defaultOrder,
    defaultSiteRateScheduleId,
    flags,
    freightRatesRef,
    freightRatesSectionRef,
    fuelSurchargeRateDate,
    isCompletedOrder,
    isCopyAndEdit,
    isEditingOrder,
    isFreightRateRequired,
    isFreightRatesBlockVisible,
    keepFreightRatesMounted,
    onDefaultSiteRateScheduleChange,
    orderHasOwnSiteRates,
    orderSiteRateSchedules,
    preloads,
    projectData,
    projectSiteRates,
    ratesSyncStatus,
    shouldOverrideRatesWithSrs,
    shouldUseProjectRateDefaults,
    toggleFreightRatesBlockVisibility,
    useVendorRateForDriver,
    watchedCompany,
    watchedProject,
  } = useOrderFormV3Context();
  const { control } = useFormContext<OrderFormV3FieldValues>();

  // Not an order field: it steers the save's cascade, so it never enters the schema/payload. The
  // project's persisted `apply_rate_changes_to_existing_orders` MUST NOT be read here — that toggle
  // steers project-originated rate changes, this one order-originated; they are independent.
  const [applyOverride, setApplyOverride] = useState<boolean | undefined>(undefined);

  // Hiding the freight rates unmounts the manager, and with it the submit hook the checkbox
  // reaches the save through — so an override kept across the collapse would be silently
  // dropped at submit. Discard it with the section instead, which is what the box shows on the
  // next open.
  const toggleFreightRates = () => {
    if (isFreightRatesBlockVisible) {
      setApplyOverride(undefined);
    }
    toggleFreightRatesBlockVisibility();
  };

  // TRE-16198: a completed order only supplies its own rate cards when it actually owns a
  // stamped snapshot. Completion no longer stamps an order-scoped SiteRateSchedule — a
  // completed order stays synced to the project rate book — so a newly completed order's
  // order-scoped list comes back empty. Suppressing `projectId`
  // for every completed order left FreightRates with no source at all (empty
  // `initialSiteRateSchedules` AND no `projectId`), which is why the rates vanished on
  // complete. Gate the suppression on the snapshot actually existing so the project rate
  // book remains the fallback, mirroring the non-completed edit path in
  // useSiteRateScheduleSync.
  const completedOrderHasOwnRates = isCompletedOrder && completedOrderRules.length > 0;
  // The fallback must wait for the order-scoped lookup to resolve. `completedOrderRules` is `[]` for the
  // first renders, so forwarding `projectId` immediately would let FreightRates start its project fetch
  // and consume its one-shot `hasInitialized` guard before the stamped snapshot of an older completed
  // order arrives — showing the project's CURRENT rates on an order that owns frozen ones, with no
  // remount to recover.
  const completedOrderRatesUnresolved = isCompletedOrder && !completedOrderRulesLoaded;

  // The checkbox is meaningful exactly when the queued rate ops will write the PROJECT's
  // cards and so cascade to other orders — `resolveSiteRateScheduleTarget`'s 'project' branch. Deriving
  // it from the same inputs as the write target is the point: the previous rule tested order identity
  // (synced + has-id + not-copied) and, being an independent test, drifted from where the write actually
  // lands — hiding the control on three surfaces whose edits cascaded silently.
  //
  // Excluding an order that owns order-scoped cards covers both ways it can own them (a decoupling
  // stamp, or the clones written when it is left out of a cascade); either way edits stay order-local.
  // That covers a durable dereference: `orderOwnsSiteRates` reports ownership for any card carrying the
  // persisted `dereferenced` flag, whatever its values — the hiding is flag-based, never value-derived.
  // Completed orders need a separate ownership term because `orderHasOwnSiteRates` force-ignores
  // ownership while completed, and it must not be widened here — it also drives the FreightRates
  // remount key and the `projectId` suppression above. Completion and invoice approval are deliberately
  // NOT factors: an order still tracking the project rate book gets the checkbox either way, and on an
  // approved order the backend protects the already-billed dimensions from the resulting edit.
  const rateWriteIsProjectScoped =
    Boolean(watchedProject?.id) &&
    !orderHasOwnSiteRates &&
    !completedOrderHasOwnRates &&
    !completedOrderRatesUnresolved;

  // Steering the cascade needs `edit_project`, so the permission governs the VALUE, not just whether the
  // checkbox renders — hiding the control alone leaves a default the actor cannot escape. It must force
  // `true`: unchecked means a permanent, one-way dereference, so `false` would orphan the order on every
  // rate edit they make. The backend coerces that `true` back down to origin-only, and FreightRates
  // suppresses the sidesheet on the same permission (its confirm 403s without it).
  //
  // NOTE for anyone tempted to fold the permission into `rateWriteIsProjectScoped` instead: that skips
  // the cascade branch entirely, so NO override is sent, and the backend then falls back to the
  // project's persisted setting — with no skip list and no date bounds, a silent cascade to every
  // reachable order. Strictly worse than the abort it would be fixing.
  //
  // An empty project rate book is deliberately NOT a factor: the order still tracks the project, so a
  // save from here writes the project's first cards and cascades from them. With no cards to copy the
  // backend records no dereference — an uncheck takes effect through the order-scoped cards the save
  // writes instead, which the card resolver prefers over the project's.
  const canSteerRateCascade = rateWriteIsProjectScoped && canEditProject;
  const applyRateChangesToExistingOrders = canSteerRateCascade
    ? (applyOverride ?? applyRateChangesDefaultOn)
    : true;

  // The three per-owner default-rate triplets, derived through the shared
  // utils with the identical argument order. Only the customer owner takes
  // the primary-rate fallback (legacy passes `undefined` in the
  // vendor/driver slots).
  const rateDetailsFor = (fieldType: 'customer' | 'vendor' | 'driver') => {
    const primaryRate =
      fieldType === 'customer' ? preloads.primaryCustomerRate : undefined;
    return {
      targetRate: getOrderFormRate(
        defaultOrder,
        projectData,
        fieldType,
        projectSiteRates,
        primaryRate,
        shouldUseProjectRateDefaults,
        shouldOverrideRatesWithSrs,
      ),
      targetRateType: getOrderFormRateType(
        defaultOrder,
        projectData,
        fieldType,
        projectSiteRates,
        primaryRate,
        shouldUseProjectRateDefaults,
        shouldOverrideRatesWithSrs,
      ),
      targetRateValue: getOrderFormRateValue(
        defaultOrder,
        projectData,
        fieldType,
        projectSiteRates,
        primaryRate,
        shouldUseProjectRateDefaults,
        shouldOverrideRatesWithSrs,
      ),
    };
  };

  const defaultDigitalTicketViewBlock = showDefaultDigitalTicketView ? (
    <Box data-test-id="order-form-v3-default-digital-ticket-view">
      <CheckboxInput
        control={control}
        label={t('order.form.default_digital_ticket_view')}
        name="defaultDigitalTicketView"
        sx={{ ...checkboxSx, width: 'fit-content' }}
      />
    </Box>
  ) : null;

  const jobsSplittableBlock = flags.vendorJobSplittingEnabled ? (
    <Tooltip title={t('account.form.allow_vendor_to_split_jobs_tooltip')} placement="top">
      <Box data-test-id="order-form-v3-jobs-splittable" sx={{ width: 'fit-content' }}>
        <CheckboxInput
          control={control}
          label={t('order.form.allow_vendors_to_split_jobs')}
          name="jobsSplittable"
          sx={checkboxSx}
        />
      </Box>
    </Tooltip>
  ) : null;

  // Seeded from the selected project by useProjectCascade.
  const liveEtaBlock = flags.liveEtaEnabled ? (
    <Tooltip title={t('order.form.enable_live_eta_tooltip')} placement="top">
      <Box data-test-id="order-form-v3-live-eta" sx={{ width: 'fit-content' }}>
        <CheckboxInput
          control={control}
          label={t('order.form.enable_live_eta')}
          name="liveEta"
          sx={checkboxSx}
        />
      </Box>
    </Tooltip>
  ) : null;

  // Rendered wherever the footer matrix places it — every use site below
  // sits behind the driverAgent flag, like legacy.
  const driverAgentBlock = (
    <Tooltip title={t('order.form.chase_missing_tickets_tooltip')}>
      <Box data-test-id="order-form-v3-chase-missing-tickets">
        <CheckboxInput
          control={control}
          label={t('order.form.text_drivers_for_missing_tickets')}
          name="chaseMissingTickets"
          sx={{ ...checkboxSx, width: 'fit-content' }}
        />
      </Box>
    </Tooltip>
  );

  // Not flag-gated; the footer matrix and FreightRates' bottomContent slot
  // are mutually exclusive, so exactly one renders.
  const prevailingWageBlock = (
    <Box data-test-id="order-form-v3-prevailing-wage">
      <CheckboxInput
        control={control}
        label={t('order.form.prevailing_wage')}
        name="prevailingWage"
        sx={{ ...checkboxSx, width: 'fit-content' }}
      />
    </Box>
  );

  // TRE-15829: the order-form surface of the selective-rate-apply checkbox. It
  // renders exactly when the actor can steer the cascade, which is the same
  // condition that lets the checkbox's value mean anything. The label is shared
  // with the project form so it reads correctly on every surface, including create.
  const applyRateChangesBlock = canSteerRateCascade ? (
    <Tooltip title={t('order.form.apply_rate_changes_to_existing_orders_tooltip')}>
      <Box
        data-test-id="order-form-v3-apply-rate-changes-to-existing-orders"
        sx={{ width: 'fit-content' }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={applyRateChangesToExistingOrders}
              onChange={() => {
                const nextChecked = !applyRateChangesToExistingOrders;
                AnalyticsController.track('apply_rate_changes_toggled', {
                  scope: 'order',
                  checked: nextChecked,
                });
                setApplyOverride(nextChecked);
              }}
              // Match the v2 CheckboxInput's padding (default 9px left, 2px right,
              // no vertical) so this raw checkbox aligns with Prevailing Wage above.
              sx={{ pr: '2px', py: 0 }}
            />
          }
          label={
            <Typography>
              {t('order.form.apply_rate_changes_to_existing_orders')}
            </Typography>
          }
          sx={checkboxSx}
        />
      </Box>
    </Tooltip>
  ) : null;

  // The remount key mirrors legacy: when the SRS source flips between
  // project- and order-owned, FreightRates must re-initialize from the new
  // source instead of holding stale data behind its hasInitialized guard.
  const freightRatesBlock = (
    <Box
      ref={freightRatesSectionRef}
      data-test-id="order-form-v3-freight-rates"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <FreightRates
        key={`srs-source:${
          orderHasOwnSiteRates
            ? `order-srs:${defaultOrder?.id ?? 'new'}`
            : (watchedProject?.id ?? `order:${defaultOrder?.id ?? 'new'}`)
        }`}
        ref={freightRatesRef}
        companyId={watchedCompany?.id ?? defaultOrder?.company?.id}
        initiallyExposeDriverRate={true}
        isEditingOrder={isEditingOrder}
        isCopyAndEdit={isCopyAndEdit}
        stripSrsIds={!watchedProject?.id || orderHasOwnSiteRates}
        ratesSyncStatus={ratesSyncStatus}
        projectId={
          completedOrderRatesUnresolved ||
          completedOrderHasOwnRates ||
          orderHasOwnSiteRates
            ? undefined
            : (watchedProject?.id ?? undefined)
        }
        initialSiteRateSchedules={
          completedOrderHasOwnRates
            ? completedOrderRules
            : orderHasOwnSiteRates
              ? orderSiteRateSchedules
              : undefined
        }
        defaultDriverRateDetails={rateDetailsFor('driver')}
        useVendorRateForDriver={useVendorRateForDriver}
        orderDate={fuelSurchargeRateDate}
        onDirtyStatusChange={callbacks.onDirtyStatusChange}
        bottomContent={
          /* Single flex child: FreightRates' root column applies its card-sized
             gap between each child, which read as excessive padding between
             these two related checkboxes (mirrors the project FreightRatesPanel). */
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {prevailingWageBlock}
            {applyRateChangesBlock}
          </Box>
        }
        defaultSiteRateScheduleId={defaultSiteRateScheduleId}
        onDefaultSiteRateScheduleChange={onDefaultSiteRateScheduleChange}
        sourceStarredSiteRateSchedule={
          isCopyAndEdit ? (defaultOrder?.defaultSiteRateSchedule ?? null) : null
        }
        scope="order"
        originOrderId={isCopyAndEdit ? undefined : defaultOrder?.id}
        applyRateChangesToExistingOrders={applyRateChangesToExistingOrders}
        orderRateWriteIsProjectScoped={rateWriteIsProjectScoped}
        canEditProject={canEditProject}
        isFreightRateRequired={isFreightRateRequired}
      />
    </Box>
  );

  return (
    <Card sx={{ p: 2, mb: 2, overflow: 'visible' }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('form_fields.trucks')}
      </Typography>

      <TrucksV2
        leftColumnBottomContent={
          defaultDigitalTicketViewBlock || jobsSplittableBlock || liveEtaBlock ? (
            <>
              {defaultDigitalTicketViewBlock}
              {jobsSplittableBlock}
              {liveEtaBlock}
            </>
          ) : undefined
        }
        order={defaultOrder ?? undefined}
        parent={projectData ?? undefined}
        shouldDirtyDerivedWrites
        tomlinsonOrderModalFeatureFlagEnabled={flags.tomlinsonOrderModal}
      />

      {keepFreightRatesMounted ? (
        // A required freight rate is validated inside FreightRates, so it stays
        // mounted whatever the rate-card flags or the collapse toggle say.
        <>
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1.5 }} />
          </Grid>
          {flags.driverAgent && <Grid size={{ xs: 12 }}>{driverAgentBlock}</Grid>}
          {freightRatesBlock}
        </>
      ) : flags.showHaulerRateTypeCard ? (
        // TRE-15597: showHaulerRateTypeCard swaps the freight-rates UI for the
        // hauler-rate-card (RateDetailsPanel), so the FreightRates collapsible
        // is suppressed here — only the driver-agent checkbox may remain.
        // Mirrors legacy. tomlinsonOrderModal keeps its own arms below (it
        // also hides the collapsible), so this raw-flag check matches legacy
        // exactly for every flag combination.
        flags.driverAgent ? (
          <>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1.5 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>{driverAgentBlock}</Grid>
          </>
        ) : null
      ) : flags.tomlinsonOrderModal && !flags.driverAgent ? (
        prevailingWageBlock
      ) : (
        <>
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1.5 }} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            {flags.tomlinsonOrderModal ? (
              driverAgentBlock
            ) : (
              <Box
                sx={{
                  alignItems: 'top',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <PseudoLink
                  action={toggleFreightRates}
                  title={
                    isFreightRatesBlockVisible
                      ? t('order.form_v2.hide_freight_rates')
                      : t('order.form_v2.show_freight_rates')
                  }
                  sx={{
                    ...(isFreightRatesBlockVisible ? { mb: 3 } : {}),
                  }}
                />
                {flags.driverAgent && driverAgentBlock}
              </Box>
            )}
          </Grid>

          {isFreightRatesBlockVisible ? freightRatesBlock : null}
          {!isFreightRatesBlockVisible && prevailingWageBlock}
        </>
      )}
    </Card>
  );
}
